import { route as brandRoute } from "@faire/web-api/routes/brand/brandToken";
import { route as c1Route } from "@faire/web-api/routes/category/c1CategoryUrlId";
import { route as c1BrandValueRoute } from "@faire/web-api/routes/category/c1CategoryUrlId/brand-value/brandValue";
import { route as c2Route } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId";
import { route as c2BrandValueRoute } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/brand-value/brandValue";
import { route as c3Route } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId";
import { route as c3BrandValueRoute } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId/brand-value/brandValue";
import { route as discoverRoute } from "@faire/web-api/routes/discover/searchTerm";
import { route as exploreRoute } from "@faire/web-api/routes/explore/searchTerm";
import { route as collectionRoute } from "@faire/web-api/routes/product-based-collection/product_based_collection_token";
import { route as productRoute } from "@faire/web-api/routes/product/productToken";
import { route as homeRoute } from "@faire/web-api/routes/root";
import { route as searchRoute } from "@faire/web-api/routes/search";
import { route as suppliersC1Route } from "@faire/web-api/routes/suppliers/c1SuppliersUrlId";
import { route as suppliersC1BrandValueRoute } from "@faire/web-api/routes/suppliers/c1SuppliersUrlId/brand-value/brandValue";
import { route as suppliersC2Route } from "@faire/web-api/routes/suppliers/c1SuppliersUrlId/subcategory/c2SuppliersUrlId";
import { route as suppliersC3Route } from "@faire/web-api/routes/suppliers/c1SuppliersUrlId/subcategory/c2SuppliersUrlId/c3SuppliersUrlId";
import { delay } from "@faire/web/common/delay";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import {
  getWindow,
  getWindowOrThrow,
} from "@faire/web/common/globals/getWindow";
import { getCurrentPath } from "@faire/web/common/Path";
import { RouteMatcher } from "@faire/web/common/routes/RouteMatcher";
import { Storage } from "@faire/web/common/Storage";

import { HAS_CLOSED_SIGNUP_MODAL } from "@faire/retailer/components/AppSignUp/AppSignUpState";
import {
  notifySignupSnackbarEvent,
  SignUpSnackbarEvent,
} from "@faire/retailer/components/LoggedOutMobileSignUpSnackbar/useSignupSnackbarStateMachine";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { isComingFromFacebookOrInstagram } from "@faire/retailer/lib/navigation-origin/isComingFromFacebookOrInstagram";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { getIsProduction } from "@faire/retailer/serialized-data/getEnvName";
import {
  assignSettingAutoSignUpModalTiming,
  getSettingAutoSignUpModalTiming,
} from "@faire/retailer/settings/getSettingBatchedLowLiftTest5";
import { AppModalStore } from "@faire/retailer/stores/ui/AppModalStore";

import { getFaireDirectBrandToken } from "../serialized-data/getFaireDirectBrandToken";

let debouncedAutoSignUp: (() => void) | undefined = undefined;

export const initializeAutoSignUp = async () => {
  if (shouldPopSignUp()) {
    // only assign setting if user is eligible to see the modal
    // then we will show the modal after the timing
    assignSettingAutoSignUpModalTiming();
  }
  if (debouncedAutoSignUp === undefined) {
    debouncedAutoSignUp = debounceIfPossible(
      autoSignUp,
      getSettingAutoSignUpModalTiming()
    );
  }
  getWindow()?.addEventListener("scroll", debouncedAutoSignUp);

  // If we've scrolled before page load (i.e., while browsing SSR content),
  // we want to behave as if debouncedAutoSignUp() was called.
  const lastScrollBeforePageLoad = getWindow()?.mostRecentScrollTime;
  if (lastScrollBeforePageLoad) {
    const elapsed = performance.now() - lastScrollBeforePageLoad;
    await delay(Math.max(getSettingAutoSignUpModalTiming() - elapsed, 0));

    if (lastScrollBeforePageLoad === getWindow()?.mostRecentScrollTime) {
      autoSignUp();
    }
  }
};

export const autoSignUp = () => {
  if (shouldPopSignUp() && debouncedAutoSignUp !== undefined) {
    notifySignupSnackbarEvent(SignUpSnackbarEvent.AUTO_SIGNUP_OPENED);
    getWindowOrThrow().removeEventListener("scroll", debouncedAutoSignUp);
    popSignUp(SignUpType.AUTO, () => {}, {
      element: SignUpElement.AUTO_POPUP,
      sourceId: "auto_signup",
    });
  }
};
const shouldPopSignUp = () => {
  const matcher = new RouteMatcher(whitelist);
  const isFaireDirectActiveInLoggedOut = !!getFaireDirectBrandToken();
  return (
    getIsProduction() &&
    !isLoggedInRetailerOrBrandPreview() &&
    !isComingFromFacebookOrInstagram() &&
    !isFaireDirectActiveInLoggedOut &&
    !AppModalStore.get().isModalShowing &&
    matcher.match(getCurrentPath().pathname) &&
    !Storage.session.getItem(HAS_CLOSED_SIGNUP_MODAL)
  );
};

const whitelist = [
  homeRoute,
  brandRoute,
  productRoute,
  collectionRoute,
  searchRoute,
  discoverRoute,
  exploreRoute,
  c1Route,
  c1BrandValueRoute,
  c2Route,
  c2BrandValueRoute,
  c3Route,
  c3BrandValueRoute,
  suppliersC3Route,
  suppliersC1Route,
  suppliersC1BrandValueRoute,
  suppliersC2Route,
];
